import React, { useState } from 'react'
import EmptyPage from 'layout/access-management/empty-page'
import Page from 'layout/access-management/page'
import PageTitle from 'layout/page-title'
import { split } from 'lodash'
import { usePermission } from 'api/hooks/use-permissions'
import { SuperUserPermissionBadge, HighRiskPermissionBadge } from 'shared/badges'
import HighRiskToggle from 'duties/permissions/components/HighRiskToggle'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Badge from 'react-bootstrap/Badge'
import EmployeesTab from 'duties/permissions/employees-tab'
import RolesTab from 'duties/permissions/roles-tab'
import AuditLogTab from 'duties/permissions/audit-log-tab'
import Tabs from 'duties/shared/tabs'
import { faFolder } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const displayCaption = (caption) => {
  return caption ? ` - ${caption}` : ''
}

function PermissionValueBadge({ permissionValue, className }) {
  return (
    <Badge key={`permission-value-${permissionValue.id}`} className={`border me-2 mb-1 ${className}`}>
      {`${permissionValue.value}${displayCaption(
        permissionValue.value_caption
      )}`}
    </Badge>
  )
}

function Permission() {
  const path = window.location.pathname
  const permissionId = split(path, '/')[2]
  const [activeTab, setActiveTab] = useState('employees')

  const params = {
    id: permissionId
  }

  const { isSuccess, isLoading, data: permission } = usePermission({ params })

  // isSuccess as well as isLoading: on a failed fetch `permission` is undefined
  // and the permission_values reads below throw, taking the whole page down.
  if (isLoading || !isSuccess) {
    return <EmptyPage />
  }

  const crumbs = [
    { name: 'Access Management', url: 'duties' },
    { name: 'Permissions', url: 'permissions' },
    { name: isSuccess ? permission.name : '', url: '' }
  ]

  const hasPermissionValues = permission.permission_values.length > 0
  const expansivePermissionValues = permission.permission_values.filter((pv) => pv.is_expansive)
  const notExpansivePermissionValues = permission.permission_values.filter((pv) => !pv.is_expansive)

  return (
    <Page title={`Permission`} subtitle={''}>
      <PageTitle breadcrumbs={crumbs} />
      {isSuccess && (
        <>
          <h4 className="my-4">{permission.description}</h4>

          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <Row>
                    <Col sm={2}>
                      <span className="fw-bold">Functional Area</span>
                      <div className="fw-light mt-2">
                        <FontAwesomeIcon
                          icon={faFolder}
                          className="text-muted me-2"
                        />
                        {permission.category_caption}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="fw-bold">Identifier</div>
                      <div className="fw-light mt-2">{permission.name}</div>
                    </Col>
                    <Col sm={4}>
                      <div className="fw-bold mb-2">Tags</div>
                      {!permission.is_superuser && (
                        <span className="text-muted">None</span>
                      )}
                      {permission.is_superuser && (
                        <SuperUserPermissionBadge
                          count={1}
                          displayCount={false}
                        />
                      )}
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col sm={8}>
                      <div className="fw-bold mb-2">Additional Details</div>
                      <span className="text-muted">
                        {permission.long_description}
                      </span>
                    </Col>
                    <Col sm={4}>
                      <HighRiskToggle permission={permission} />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    {hasPermissionValues ? (
                      <>
                        <div className="me-2 fw-bold mb-2">
                          Permission Values
                        </div>
                        <Col sm={3}>
                          <div className="mb-2 border-end pe-4">
                            <div className="me-2 border-bottom mb-2 pb-1">
                              Expansive
                            </div>
                            {expansivePermissionValues.map((pv) => (
                              <PermissionValueBadge permissionValue={pv} className={'badge-primary text-primary'} />
                            ))}
                          </div>
                        </Col>
                        <Col sm={3}>
                          <div>
                            <div className="me-2 border-bottom mb-2 pb-1">
                              Not Expansive
                            </div>
                            {notExpansivePermissionValues.map((pv) => (
                              <PermissionValueBadge permissionValue={pv} className={'badge-secondary text-info'} />
                            ))}
                          </div>
                        </Col>
                      </>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              <Row className="mt-4">
                <Col sm={12}>
                  <div className="d-flex mb-3">
                    <Tabs
                      tabs={[
                        { name: 'Users', value: 'employees' },
                        { name: 'Roles', value: 'roles' },
                        { name: 'Audit Log', value: 'audit' }
                      ]}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}

      {activeTab === 'employees' && (
        <EmployeesTab permissionId={permissionId} />
      )}

      {activeTab === 'roles' && <RolesTab permissionId={permissionId} />}

      {activeTab === 'audit' && <AuditLogTab permission={permission} />}
    </Page>
  )
}

export default Permission
